import appconfig from '@/appconfig'
import logger from '../../logger'
import type { Primus, PrimusOutgoingUrl } from '@/types/primus';
import type { WSBotMessage, WSCommand, WSHistoryMessage, WSLoopbackMessage } from '@/types/WSBotMessage';
import { getJWTToken } from './token';
import type { BackendMessage } from './backendMessageBuilder';


let primusInstance: Primus | undefined = undefined;
let hasActiveConnection = false
let channel = ''

const outgoingURLHandler = (url: PrimusOutgoingUrl) => {
  url.query = 'referrer=local'
  url.query += `&bot=${channel || ''}`
}

interface ConnectParams {
  primusUrl: string
  channelId: string
  /* dataHandler: (data: unknown) => void */
  connectionStatusHandler: (status: string) => void
  onUUIDLoopback: (uuid: string, tempIp: string) => void
  onBotMessage: (data: WSBotMessage) => void
  onHistoryArray: (data: WSHistoryMessage[]) => void
  onBotCommand: (data: WSCommand) => void
  onNewSession: (firstMessage: WSBotMessage) => void
  handleMessageInject: (data: WSBotMessage) => void

}

type WSMessages = WSBotMessage | WSCommand | WSHistoryMessage[] | WSLoopbackMessage

const isNewSession = () => !getJWTToken()
const isBotCommand = (data: WSMessages): data is WSCommand => 'recipient' in data && 'api' in data
const isLoopback = (data: WSMessages): data is WSLoopbackMessage => 'tempId' in data && 'uuid' in data
const isHistoryData = (data: WSMessages): data is WSHistoryMessage[] => Array.isArray(data)
const isEmptyMessage = (data: unknown): boolean => {
  const msg = data as { isFromClient?: boolean; data?: { message?: string } }
  return !!msg.isFromClient && !msg.data?.message
}
const isBotMessage = (data: WSMessages): data is WSBotMessage => 'recipient' in data && 'sender' in data && 'time' in data

export const connect = async (options: ConnectParams) => {
  return new Promise((resolve, reject) => {
    const { primusUrl, /* dataHandler, */ connectionStatusHandler, channelId,
      onUUIDLoopback, onBotMessage, onHistoryArray, onBotCommand, onNewSession, handleMessageInject} = options
    if (!window.Primus) reject(new Error('Primus not loaded'))
    if (hasActiveConnection) reject(new Error('already connected'))

    channel = channelId
    try {
      primusInstance = new window.Primus(primusUrl, {
        reconnect: {
          max: Infinity, // Number: The max delay before we try to reconnect.
          min: 500, // Number: The minimum delay before we try reconnect.
          retries: 100 // Number: How many times we should try to reconnect.
        }
      })

      primusInstance.on('outgoing::url', outgoingURLHandler)
      // we need this handler here to resolve the promise that the connection
      // was successful and is open and ready to go
      primusInstance.on('open', () => {
        hasActiveConnection = true
        console.log('primus open', hasActiveConnection)
        // TODO dont do this here
        // connected(initialMessage);
        resolve(true)
      })
      if (appconfig.isDevelopment) {
        // INJECT DEBUG CHANNEL
        // COPY FOR NOW - TODO
        const bc = new BroadcastChannel('moin.debug')
        bc.onmessage = async (ev) => {
          const data = ev.data
          /* conversation.value.set(uuidv4(), ev.data); */
          console.log('broadcast channel', ev.data)
          if (Array.isArray(data) && data.length === 0) return
          if (isEmptyMessage(data)) return
          if (isNewSession()) {
            const firstMessage = Array.isArray(data) ? data[0].data : data
            onNewSession(firstMessage)
          }
          if (isBotCommand(data)) onBotCommand(data as WSCommand)
          if (isLoopback(data)) onUUIDLoopback((data as WSLoopbackMessage).tempId, (data as WSLoopbackMessage).uuid)
          if (isHistoryData(data)) onHistoryArray(data as WSHistoryMessage[])
          // TEST THIS
          if (isBotMessage(data)) onBotMessage(data as WSBotMessage)
          if (isBotMessage(data)) handleMessageInject(data as WSBotMessage)

        }
      }
      primusInstance.on('data', (data: WSMessages) => {
        // we ignore all messages that are empty or bogus
        if (Array.isArray(data) && data.length === 0) return
        if (isEmptyMessage(data)) return
        if (isNewSession()) {
          const firstMessage = Array.isArray(data) ? data[0].data : data
          onNewSession(firstMessage as WSBotMessage)
        }
        if (isBotCommand(data)) onBotCommand(data as WSCommand)
        if (isLoopback(data)) onUUIDLoopback((data as WSLoopbackMessage).tempId, (data as WSLoopbackMessage).uuid)
        if (isHistoryData(data)) onHistoryArray(data as WSHistoryMessage[])
        // TEST THIS
        if (isBotMessage(data)) onBotMessage(data as WSBotMessage)
      })

      primusInstance.on('open', () => {
        connectionStatusHandler(statusHandler(null, 'online'))
      })
      primusInstance.on('offline', () => {
        connectionStatusHandler(statusHandler(null, 'offline'))
      })
      primusInstance.on('reconnect failed', () => {
        connectionStatusHandler(statusHandler(null, 'reconnect failed'))
      })
      primusInstance.on('reconnect', () => {
        connectionStatusHandler(statusHandler(null, 'reconnect'))
      })
      /* primusInstance.on('reconnected', () => {
        connectionStatusHandler(null, 'reconnected');
      }); */
      primusInstance.on('error', (error: Error) => {
        connectionStatusHandler(statusHandler(error, 'error'))
      })

    } catch (error) {
      logger.log('[ER] error initializing primus', error)
      reject(error)
    }
  })
}




export const send = (message: BackendMessage) => {
  if (!primusInstance && !isConnected) {
    logger.warn('primus is not connected or not intialized - no message send', message)
    return
  }

  // Extend message with widget version
  message.version = appconfig.appVersion
  primusInstance.write(message)
}

export const disconnect = () => {
  primusInstance.destroy({timeout: 10000})
  primusInstance = null
}

export const end = () => {
  if (primusInstance && primusInstance.end) {
    primusInstance.end()
    primusInstance = null
  }
}

export const isConnected = () => !!hasActiveConnection

const statusHandler = (error: unknown, status: string) => {
  if (error) {
    logger.error('[ER] primus error', error)
    console.error(error)
    return 'error'
  }
  switch (status) {
    case 'online':
      return 'online'
    case 'offline':
      return 'offline'
    case 'reconnect failed':
      return 'offline'
    case 'reconnect':
      return 'reconnect'
    case 'reconnected':
      return 'online'
    case 'error':
      return 'offline'
    default:
      logger.warn(' [transport] this should never happen', status)
  }
  return status
}

export default {
  disconnect,
  end,
  send,
  connect,
  isConnected
}
